import { type Customer, type CustomerListQuery, CustomerStatuses, routes } from '@pmai/contracts';
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  PageHeader,
  SelectField,
  TextField,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useApiQuery } from '../../shared/api/hooks';
import { Can } from '../../shared/auth/session';
import { useListParams, useSearchBox } from '../../shared/list/use-list-params';
import { useConfirm } from '../../shared/use-confirm';
import { CustomerDialog } from './customer-dialog';
import { ExternalLink } from '../../shared/external-link';
import { CustomerStatusBadge } from './customer-parts';

/** Customer list — FR-CUST-001…007 (F1 slice). */
export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useListParams<CustomerListQuery['sort'], 'status'>({
    sort: 'name',
    filters: ['status'],
  });
  const search = useSearchBox(list.q, (q) => list.set({ q }));
  const [editing, setEditing] = useState<Customer | 'new' | null>(null);

  const status = CustomerStatuses.codes.find((c) => c === list.filters.status);
  const customers = useApiQuery(
    routes.customers.list,
    {
      query: {
        q: list.q || undefined,
        status,
        sort: list.sort,
        order: list.order,
        offset: list.offset,
        limit: list.limit,
      },
    },
    { placeholderData: (previous) => previous },
  );
  const remove = useConfirm(routes.customers.remove, {
    invalidates: [routes.customers.list],
    params: (c: Customer) => ({ id: c.id }),
  });

  return (
    <>
      <PageHeader
        title={t('customers.title')}
        actions={
          <Can perm="customers.edit">
            <Button icon="plus" onClick={() => setEditing('new')}>
              {t('customers.new')}
            </Button>
          </Can>
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_16rem]">
        <TextField label={t('common.search')} type="search" placeholder={t('customers.search')} {...search} />
        <SelectField
          label={t('customers.fields.status')}
          placeholder={t('customers.allStatuses')}
          value={list.filters.status}
          options={CustomerStatuses.options.map((o) => ({ value: o.code, label: t(o.labelKey) }))}
          onChange={(e) => list.set({ status: e.target.value })}
        />
      </div>
      <DataTable
        label={t('customers.title')}
        rows={customers.data?.rows}
        rowKey={(c) => c.id}
        loading={customers.isFetching}
        empty={<EmptyState icon="building" title={t('customers.empty')} />}
        sort={{ sort: list.sort, order: list.order }}
        onSortChange={(s) => list.set({ sort: s.sort, order: s.order })}
        paging={{
          offset: list.offset,
          limit: list.limit,
          total: customers.data?.total ?? 0,
          onChange: (offset) => list.set({ offset }),
        }}
        columns={[
          {
            key: 'name',
            header: t('customers.fields.name'),
            sortKey: 'name',
            cell: (c) => (
              <Link to={c.id} className="font-medium text-brand-700 hover:underline">
                {c.name}
              </Link>
            ),
          },
          {
            key: 'status',
            header: t('customers.fields.status'),
            sortKey: 'status',
            cell: (c) => <CustomerStatusBadge status={c.status} />,
          },
          {
            key: 'contract',
            header: t('customers.fields.contract'),
            cell: (c) => <ExternalLink href={c.contractUrl} label={t('customers.contractLink')} />,
          },
          {
            key: 'actions',
            header: t('common.actions'),
            align: 'right',
            cell: (c) => (
              <div className="flex justify-end gap-1">
                <IconButton
                  icon="eye"
                  label={`${t('common.view')}: ${c.name}`}
                  onClick={() => void navigate(c.id)}
                />
                <Can perm="customers.edit">
                  <IconButton
                    icon="pencil"
                    label={`${t('common.edit')}: ${c.name}`}
                    onClick={() => setEditing(c)}
                  />
                  <IconButton
                    icon="trash-2"
                    label={`${t('common.delete')}: ${c.name}`}
                    onClick={() => remove.ask(c)}
                  />
                </Can>
              </div>
            ),
          },
        ]}
      />
      <CustomerDialog
        open={editing !== null}
        customer={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        {...remove.dialog}
        title={t('customers.deleteTitle')}
        message={t('customers.deleteText', { name: remove.target?.name ?? '' })}
        confirmWord="DELETE"
        confirmLabel={t('common.delete')}
      />
    </>
  );
}
